import React, { useEffect, useState } from "react";
import "../../components/css/AddOfficerModal.css";
import useAirlineAdminApi from "../../services/api/useAirlineAdminApi";
import AddOfficersInputField from "./AddOfficersInputField";
import SnackbarAlert from "./SnackbarAlert";

const AddOfficerModal = ({
  cfpPhase,
  onAddOfficer,
  onClose,
  airportId,
  existingOfficers = [],
  editingOfficer = null,
}) => {
  const [officer, setOfficer] = useState({
    officerId: "",
    hoursWorked: "",
    name: "",
    employeeCode: "",
  });
  const [officersList, setOfficersList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const { getOfficersByAirport } = useAirlineAdminApi();

  useEffect(() => {
    const loadOfficers = async () => {
      try {
        setIsLoading(true);
        const response = await getOfficersByAirport(airportId);
        setOfficersList(response.data || []);
      } catch (error) {
        console.error("Failed to fetch officers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOfficers();
  }, [airportId]);

  useEffect(() => {
    if (editingOfficer) {
      setOfficer({
        officerId: editingOfficer.officerId,
        hoursWorked: editingOfficer.hoursWorked.toString(),
        name: editingOfficer.name,
        employeeCode: editingOfficer.employeeCode,
      });
    }
  }, [editingOfficer]);

  const handleOfficerChange = (e) => {
    const { name, value } = e.target;
    if (name === "officerId") {
      const selectedOfficer = officersList.find(
        (o) => o.id === value || o.id === parseInt(value)
      );

      setError(""); // Clear any previous errors
      setOfficer({
        ...officer,
        officerId: selectedOfficer?.id || "",
        name: selectedOfficer?.name || "",
        employeeCode:
          selectedOfficer?.employeeCode || selectedOfficer?.code || "",
      });
    } else {
      setOfficer({ ...officer, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!officer.officerId || !officer.hoursWorked) {
      setError("");
      setTimeout(() => setError("Please fill in all required fields"), 0);
      return;
    }
    const hoursWorked = parseInt(officer.hoursWorked, 10);
    if (hoursWorked < 2) {
      setError("");
      setTimeout(() => setError("Officers must work a minimum of 2 hours"), 0);
      return;
    }
    const isDuplicate = existingOfficers.some(
      (existingOfficer) =>
        existingOfficer.officerId === officer.officerId ||
        existingOfficer.officerId === parseInt(officer.officerId)
    );

    if (isDuplicate) {
      setError("");
      setTimeout(() => setError("This officer has already been added"), 0);
      return;
    }

    const officerToAdd = {
      ...officer,
      hoursWorked: hoursWorked,
      cfpPhase: cfpPhase,
    };

    onAddOfficer([officerToAdd]);
    setOfficer({
      officerId: "",
      hoursWorked: "",
      name: "",
      employeeCode: "",
    });
    setError("");
    onClose();
  };

  return (
    <div className="add-officer-modal">
      {error && <SnackbarAlert type="error">{error}</SnackbarAlert>}

      <h2 className="add-officer-modal-title">
        {editingOfficer ? "Edit Officer" : "Add Officer"}
      </h2>
      <p className="add-officer-modal-description">
        Please add details to approve special onboarding for an officer. At
        least 2 officers must be added in total.
      </p>

      <form className="add-officer-modal-form" onSubmit={handleSubmit}>
        <div>
          <p className="add-officer-modal-label">Officer Details</p>

          <select
            className="add-officer-input-field"
            name="officerId"
            value={officer.officerId || ""}
            onChange={handleOfficerChange}
            required
            disabled={isLoading}
          >
            <option value="">Select an officer</option>
            {officersList.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>

          <AddOfficersInputField
            type="number"
            name="hoursWorked"
            label="Total Number of Hours"
            min="2"
            onKeyDown={(e) => {
              if (
                e.key === "-" ||
                e.key === "e" ||
                e.key === "E" ||
                e.key === "."
              ) {
                e.preventDefault();
              }
            }}
            placeholder="Enter number of hours (min. 2)"
            value={officer.hoursWorked}
            onChange={(e) => {
              const value = e.target.value;
              if (
                value === "" ||
                (parseInt(value) >= 0 && !value.includes("."))
              ) {
                handleOfficerChange(e);
              }
            }}
            required
          />

          {officer.officerId && (
            <p className="employee-code">
              Employee Code: {officer.employeeCode}
            </p>
          )}
        </div>

        <button type="submit" className="add-officer-button">
          {editingOfficer ? "Save" : "Add"}
        </button>
      </form>
    </div>
  );
};

export default AddOfficerModal;
